import type { Observable } from 'rxjs';
import type { WidgetRuleErrors } from './widgets-errors.selectors';
import type { Uuid } from '@common/types';
import type { WidgetErrorItem } from '@common/types/widget-errors.type';
import type { WidgetsErrorsState } from './widgets-errors.reducer';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import WidgetsErrorsActions from './widgets-errors.actions';
import * as WidgetsErrorsSelectors from './widgets-errors.selectors';

@Injectable()
export class WidgetsErrorsFacade {
    private readonly store = inject(Store);
    private readonly translateService = inject(TranslateService);
    private readonly widgetsService = inject(WebWidgetsService);

    /**
     * Errors of every widget on the page: the stored ones of widgets still validated by their control,
     * plus the publish checks of those validated by their signal form, derived from their data.
     */
    public readonly widgetsErrors$: Observable<WidgetsErrorsState> = combineLatest([
        this.store.select(WidgetsErrorsSelectors.selectWidgetsErrorsState),
        this.store.select(WidgetsErrorsSelectors.selectWidgetsRuleErrors),
    ]).pipe(
        map(([stored, ruleErrors]) => ({ ...stored, ...this.toErrorItems(ruleErrors) })),
        shareReplay({ bufferSize: 1, refCount: true })
    );
    public readonly widgetErrorsAsArray$: Observable<WidgetErrorItem[]> = this.widgetsErrors$.pipe(
        map((errors) => Object.values(errors))
    );
    public readonly widgetErrorsById$ = (id: Uuid): Observable<WidgetErrorItem> => this.widgetErrorById$(id);

    public widgetErrorById$(id: Uuid): Observable<WidgetErrorItem> {
        return this.widgetsErrors$.pipe(
            map((errors) => errors[id]),
            distinctUntilChanged()
        );
    }

    public updateWidgetsErrors(id: Uuid, error: WidgetErrorItem): void {
        this.store.dispatch(WidgetsErrorsActions.updateWidgetError({ id, error }));
    }

    private toErrorItems(ruleErrors: Record<Uuid, WidgetRuleErrors>): WidgetsErrorsState {
        return Object.fromEntries(
            Object.entries(ruleErrors).map(([id, { name, messages }]) => [
                id,
                {
                    label: this.translateService.instant(this.widgetsService.getWidgetModel(name)?.label ?? ''),
                    errors: messages.map((message) => ({ key: this.translateService.instant(message) })),
                },
            ])
        );
    }
}
